import { globalStyle, style } from '@vanilla-extract/css';
import { vars } from '@/shared/ui/theme/contract.css';

/**
 * 후보 표 (퀘스트 7ca8908c) — 섭외 IA §3-3.
 *
 * 퀘스트 창 표준 1000×600 에서 이 표가 쓸 수 있는 가로는 558 이다(§3-2). 그래서 칸을 다섯으로
 * 묶고 팔로워만 숫자 정렬을 준다 — 가로 스크롤은 로스터 쪽에서 이미 쓰고 있어 여기까지 오면
 * 담당자가 두 축으로 밀게 된다.
 */
/*
  발송 이력 표와 같은 판이다(2026-09-28). 한 면에 표가 둘인데 글자 크기·칸 여백·머리 모양이
  달라 다른 화면에서 온 것처럼 보였다. 기준은 발송 이력 쪽이다.
*/
/**
 * 표는 **자기 높이 안에서 스크롤한다** (결정 2026-09-29).
 *
 * 한 페이지가 최대 500행이다(read 계약 §8 `limit ≤ 500`, 프론트는 `limit` 을 안 보내 원천
 * 기본값을 쓴다 — 그 값은 이 레포에서 못 잰다). 표가 면 전체의 스크롤(`ScenarioRosterCover`
 * 의 `framedPanel`)을 같이 쓰면 그 아래 형제인 「지금 답할 회신」·「발송 이력」 이 수천 픽셀
 * 밑으로 밀려 닿지 않는다. 그래서 표만 자기 칸에서 스크롤하고 형제는 제자리에 남는다.
 *
 * 3만 행을 훑게 만들려는 것이 아니다 — 그 판정은 `docs/design/roster-form-intake.md` 가
 * 「3만은 스크롤로 훑는 대상이 아니다」 로 닫아 뒀고(1px 드래그 ≈ 34행 · Ctrl+F 가 죽는다),
 * 좁히는 일은 「먼저 볼 것」 묶음과 조건 전체 선택이 든다. 이 칸은 **한 페이지**의 높이만 맡는다.
 *
 * 🔴 `position: relative` 를 빼지 말 것. 안의 `caption` 이 `position: absolute` 로 숨은
 * 칸이라, 기준 칸이 없으면 스크롤 칸을 탈출해 바깥 레이아웃을 밀어낸다.
 */
export const scroller = style({
  position: 'relative',
  // 머리줄 + 여덟 행 어림(행 높이 ≈ 44px). 행이 적으면 그만큼만 차지한다.
  maxHeight: 400,
  overflowY: 'auto',
  border: `1px solid ${vars.surface.border.subtle}`,
  borderRadius: vars.spacing.borderRadius.lg,
});

/**
 * `border-collapse: collapse` 는 칸 테두리를 합치면서 `border-radius` 를 무시한다. `separate`
 * + `border-spacing: 0` 으로 바꾸면 모서리가 살고 칸 사이는 그대로 붙는다. 테두리와 모서리는
 * 표가 아니라 위 스크롤 칸이 든다 — 표에 두르면 아래 선이 내용 끝에 붙어 스크롤과 함께 사라진다.
 */
export const table = style({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
});

/**
 * 고정 머리줄 — 스크롤해도 열 이름이 남는다.
 *
 * 🔴 배경은 **불투명한 것**을 깐다. `th` 의 `surface.highlight.medium` 은 0.16 이라 그것만
 * 두면 밑을 지나는 행이 글자 뒤로 비친다. 선례가 둘 다 같은 방식이다 —
 * `RosterOverview`(`background.secondary`) · `ScenarioRegistry`(`background.opaque`).
 * `th` 의 옅은 색은 이 위에 겹쳐 머리줄 색으로 읽힌다.
 */
globalStyle(`${table} thead`, {
  position: 'sticky',
  top: 0,
  zIndex: vars.elevation.decorative,
  background: vars.surface.background.secondary,
});

/** 표에 이름을 준다 — 화면에는 안 보인다. */
export const caption = style({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
});

/** 머리줄 — 배경과 아래 선으로 몸통과 가른다. 발송 이력 표와 같은 값이다. */
export const th = style({
  // 위아래 `md` — 열 이름이 배경 안에서 숨을 쉬어야 머리줄로 읽힌다(2026-09-28).
  padding: `${vars.spacing.padding.md} ${vars.spacing.padding.sm}`,
  textAlign: 'left',
  // `2xs`(11px) 는 값보다 너무 작아 이름이 눌렸다. 한 눈금 위인 `xs` 로 올린다.
  fontSize: vars.typography.fontSize.xs,
  fontWeight: String(vars.typography.fontWeight.medium),
  color: vars.typography.textColor.label,
  background: vars.surface.highlight.medium,
  borderBottom: `1px solid ${vars.opacityScale.whiteOverlay.light}`,
  whiteSpace: 'nowrap',
});

export const pickHead = style([th, { width: '36px' }]);

export const row = style({
  selectors: {
    '&:hover': { background: vars.surface.card.hover },
  },
});

export const td = style({
  // 위아래 `md` — 발송 이력 표와 같은 숨이다. `xs` 면 행이 붙어 훑기가 어렵다.
  padding: `${vars.spacing.padding.md} ${vars.spacing.padding.sm}`,
  borderBottom: `1px solid ${vars.surface.border.subtle}`,
  verticalAlign: 'middle',
  selectors: {
    // 마지막 줄의 아래 선은 표 테두리와 같은 자리라 두 겹으로 굵어진다.
    'tr:last-child &': { borderBottom: 'none' },
  },
});

export const pickCell = style([td, { width: '36px' }]);

/**
 * 숫자 칸 — **좌측 정렬**이다(2026-09-28). 이 면의 표는 전부 왼쪽에서 시작한다.
 *
 * 우측 정렬은 여러 수를 위아래로 견줄 때 값이 큰데, 열 이름과 값의 시작점이 어긋나 훑는 눈이
 * 두 번 움직인다. 자릿수만 `tabular-nums` 로 맞춘다 — 발송 이력 표가 같은 이유로 그렇게 서 있다.
 */
export const numeric = style({
  textAlign: 'left',
  fontVariantNumeric: 'tabular-nums',
});

export const handle = style({
  color: vars.typography.textColor.primary,
  fontWeight: String(vars.typography.fontWeight.medium),
});

/** 값이 없는 칸과 아직 안 온 칸 — 둘 다 약하게, 없는 값을 0 처럼 보이지 않게. */
export const muted = style({
  color: vars.typography.textColor.softMuted,
});

export const noReply = style({
  color: vars.typography.textColor.tertiary,
});

/** 회신·폼 제출은 기본 제외로 넘어가는 신호라 눈에 띄어야 한다. */
export const replied = style({
  color: vars.bright.status.success,
});

export const form = style({
  color: vars.bright.status.info,
});

export const rosterLink = style({
  color: vars.bright.status.info,
  textDecoration: 'none',
  selectors: {
    '&:hover': { textDecoration: 'underline' },
  },
});
